import * as React from 'react';
import {
  XIcon,
  CheckCircleIcon,
  WarningCircleIcon,
  InfoIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils/cn';
import { createContext, useContext } from 'react';
import { useIsAnyPanelOpen } from '@/lib/hooks/use-is-any-panel-open';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  title: string;
  description?: string;
  type?: ToastType;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => string; // Returns toast ID
  updateToast: (id: string, updates: Partial<Omit<Toast, 'id'>>) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export const ToastProvider = ({ children }: { children: React.ReactNode }) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  // Track timeouts for cleanup to prevent memory leaks
  const timeoutsRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup all timeouts on unmount
  React.useEffect(() => {
    const timeouts = timeoutsRef.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
    };
  }, []);

  const removeToast = React.useCallback((id: string) => {
    // Clear the timeout when toast is manually removed
    const timeout = timeoutsRef.current.get(id);
    if (timeout) {
      clearTimeout(timeout);
      timeoutsRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const addToast = React.useCallback(
    (toast: Omit<Toast, 'id'>): string => {
      const id = Math.random().toString(36).substr(2, 9);
      const newToast: Toast = { ...toast, id };
      setToasts((prev) => [...prev, newToast]);

      const duration = toast.duration ?? 5000;
      if (duration > 0) {
        const timeout = setTimeout(() => {
          timeoutsRef.current.delete(id);
          removeToast(id);
        }, duration);
        timeoutsRef.current.set(id, timeout);
      }

      return id;
    },
    [removeToast],
  );

  const updateToast = React.useCallback((id: string, updates: Partial<Omit<Toast, 'id'>>) => {
    setToasts((prev) => prev.map((toast) => (toast.id === id ? { ...toast, ...updates } : toast)));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, updateToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

interface ToastContainerProps {
  toasts: Toast[];
  onRemove: (id: string) => void;
}

const ToastContainer = ({ toasts, onRemove }: ToastContainerProps) => {
  const isPanelOpen = useIsAnyPanelOpen();

  return (
    <div
      className={cn(
        'pointer-events-none fixed z-[100] flex max-h-screen w-full flex-col gap-1.5 p-3 sm:max-w-[320px] bottom-2',
        !isPanelOpen && 'right-2',
      )}
      style={isPanelOpen ? { right: 'calc(var(--slideover-width, 32rem) + 0.5rem)' } : undefined}
      role="region"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

interface ToastItemProps {
  toast: Toast;
  onRemove: (id: string) => void;
}

const ToastItem = ({ toast, onRemove }: ToastItemProps) => {
  const type = toast.type || 'info';

  const icons = {
    success: CheckCircleIcon,
    error: WarningCircleIcon,
    warning: WarningIcon,
    info: InfoIcon,
  };

  const Icon = icons[type];

  // Subtle accent colors for icon and left border
  const accentStyles = {
    success: {
      icon: 'text-emerald-600 dark:text-emerald-400',
      border: 'border-l-emerald-500',
    },
    error: {
      icon: 'text-red-600 dark:text-red-400',
      border: 'border-l-red-500',
    },
    warning: {
      icon: 'text-amber-600 dark:text-amber-400',
      border: 'border-l-amber-500',
    },
    info: {
      icon: 'text-blue-600 dark:text-blue-400',
      border: 'border-l-blue-500',
    },
  };

  const accent = accentStyles[type];

  return (
    <div
      className={cn(
        'group pointer-events-auto relative flex w-full items-center gap-2',
        'rounded-md border border-border/60 bg-card p-2.5 pr-7',
        'shadow-sm transition-opacity duration-150',
        'border-l-2',
        accent.border,
      )}
      role="alert"
    >
      <Icon className={cn('h-4 w-4 shrink-0', accent.icon)} weight="fill" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground leading-tight truncate">
          {toast.title}
        </div>
        {toast.description && (
          <div className="text-xs text-muted-foreground mt-0.5 leading-tight line-clamp-2">
            {toast.description}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => onRemove(toast.id)}
        className={cn(
          'absolute right-1.5 top-1.5 rounded p-0.5',
          'text-muted-foreground/60 hover:text-muted-foreground',
          'opacity-0 group-hover:opacity-100 transition-opacity',
          'focus:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring',
        )}
        aria-label="Close"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export { ToastContainer };
